<template>
  <div class="api-key-scope-box" :class="boxClass">
    <h4 class="api-key-scope-box__heading">
      {{ title }}
      <BittsCount :count="scopes.length" type="neutral" :show-zero="true" />
    </h4>
    <div v-if="scopes.length" class="api-key-scope-box__chips">
      <BittsTag
        v-for="scope in scopes"
        :key="scope"
        :text="scope"
        color="neutral"
        size="small"
      >
        <template #[chipAction.slot]>
          <button
            type="button"
            class="api-key-scope-box__chip-button"
            :class="chipAction.buttonClass"
            :aria-label="`${chipAction.label} ${scope}`"
            @click="emit('select', scope)"
          >
            <FontAwesomeIcon :icon="chipAction.icon" />
          </button>
        </template>
      </BittsTag>
    </div>
    <p v-else class="api-key-scope-box__empty">{{ emptyText }}</p>
  </div>
</template>

<script setup lang="ts">
import { BittsCount, BittsTag } from '@crossbeam/bitts';

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { computed } from 'vue';

const { title, scopes, emptyText, variant } = defineProps<{
  title: string;
  scopes: string[];
  emptyText: string;
  variant: 'selected' | 'available';
}>();

const emit = defineEmits<{
  select: [scope: string];
}>();

const CHIP_ACTIONS = {
  selected: {
    slot: 'post',
    label: 'Remove',
    icon: ['fak', 'x'],
    buttonClass: 'api-key-scope-box__chip-button--post',
  },
  available: {
    slot: 'pre',
    label: 'Add',
    icon: ['fak', 'add'],
    buttonClass: 'api-key-scope-box__chip-button--pre',
  },
} as const;

const chipAction = computed(() => CHIP_ACTIONS[variant]);

const boxClass = computed(() =>
  variant === 'selected' ? 'api-key-scope-box--selected' : '',
);
</script>

<style scoped lang="pcss">
@reference "../../../css/theme.css";

.api-key-scope-box {
  @apply flex flex-col gap-8 rounded-lg border border-solid border-neutral-border p-12;
}

.api-key-scope-box--selected {
  @apply bg-neutral-background-weak border-transparent;
}

.api-key-scope-box__heading {
  @apply flex items-center gap-8 text-sm font-bold text-neutral-text m-0;
}

.api-key-scope-box__chips {
  @apply flex flex-wrap gap-8 font-mono;
}

.api-key-scope-box__chip-button {
  @apply text-neutral-text-placeholder hover:text-neutral-text cursor-pointer text-xs;
}

.api-key-scope-box__chip-button--pre {
  @apply mr-6;
}

.api-key-scope-box__chip-button--post {
  @apply ml-6;
}

.api-key-scope-box__empty {
  @apply text-sm text-neutral-text-weak m-0;
}
</style>
